import { useEffect, useState } from 'react';
import { Form, FormItem } from 'components/Base/Form';
import { Form as AntdForm } from 'antd';
import { CustomSelect, SelectWithAdd } from 'components/Base/Select';
import { useGetOrganizationsIdentityQuery } from 'redux/apiSlice/organizations.slice';
import { useGetElcIdentitiesQuery } from 'redux/apiSlice/elcs.slice';
import CreateOrganizationModal from 'components/Shared/CreateOrganizationModal';
import { FullWidthRadioGroup, RadioGroup } from 'components/Base/RadioGroup';
import Checkbox from 'components/Base/Checkbox';
import { TOrganizationsIdentityUI } from 'data/types/organizations.types';
import { TCreateOrganizationModalOpen } from 'components/Shared/CreateOrganizationModal/CreateOrganizationModal.types';
import { Input } from 'components/Base/Input';
import GroupedEventTypeSelect from '../../GroupedEventTypeSelect';
import useGetData from './CreateEventForm.data';
import { TCreateEventFormProps } from './CreateEventForm.types';
import RegistrationFields from './RegistrationField/RegistrationFields';

enum EHostType {
  ORGANIZATION = 'ORGANIZATION',
  ELC = 'ELC',
}

const CreateEventForm = ({ form }: TCreateEventFormProps) => {
  const [open, setOpen] = useState<TCreateOrganizationModalOpen>({
    visible: false,
    initialOrgName: '',
  });
  const [selectedOrganization, setSelectedOrganization] =
    useState<TOrganizationsIdentityUI>();
  const hostType =
    AntdForm.useWatch('hostType', form) || EHostType.ORGANIZATION;

  const { createEventFormFields, hostTypeOptions } = useGetData();
  const {
    host,
    type,
    title,
    attendanceType,
    numberOfDays,
    requiresRegistration,
  } = createEventFormFields;
  const { isLoading: isLoadingOrgs, data: organizations } =
    useGetOrganizationsIdentityQuery();
  const { isLoading: isLoadingElcs, data: elcs } = useGetElcIdentitiesQuery();

  const showRegistrationFields =
    AntdForm.useWatch(requiresRegistration.name, form) || false;

  useEffect(() => {
    form.setFieldValue('host', selectedOrganization?.value);
  }, [selectedOrganization, form]);

  // Clear host field when host type changes
  useEffect(() => {
    form.setFieldValue('host', undefined);
    setSelectedOrganization(undefined);
  }, [hostType, form]);

  // Determine which data to use based on hostType
  const hostOptions =
    hostType === EHostType.ORGANIZATION ? organizations : elcs;
  const isLoadingHosts =
    hostType === EHostType.ORGANIZATION ? isLoadingOrgs : isLoadingElcs;

  const handleAddOrganization = (currentSearchValue?: string) => {
    if (hostType === EHostType.ORGANIZATION) {
      setOpen({
        visible: true,
        initialOrgName: currentSearchValue || '',
      });
    }
  };

  return (
    <>
      <Form form={form}>
        <FormItem {...type}>
          <GroupedEventTypeSelect
            placeholder={type.placeholder}
            data-cy="create-event-type-select"
          />
        </FormItem>

        <FormItem {...title}>
          <Input
            placeholder={title.placeholder}
            data-cy="create-event-title-input"
          />
        </FormItem>

        <FormItem {...attendanceType}>
          <FullWidthRadioGroup
            optionType="button"
            buttonStyle="solid"
            options={attendanceType.options}
            defaultValue={attendanceType.initialValue}
            data-cy="create-event-attendance-type-radio"
          />
        </FormItem>

        <FormItem
          label="Host Type"
          name="hostType"
          initialValue={EHostType.ORGANIZATION}
        >
          <RadioGroup
            optionType="button"
            buttonStyle="solid"
            options={hostTypeOptions}
          />
        </FormItem>

        <FormItem {...host}>
          {hostType === EHostType.ORGANIZATION ? (
            <SelectWithAdd
              placeholder={host.placeholder}
              loading={isLoadingHosts}
              options={hostOptions}
              onBtnClick={handleAddOrganization}
              addButtonLabel="Add"
              data-cy="create-event-host-select"
            />
          ) : (
            <CustomSelect
              placeholder={host.placeholder}
              loading={isLoadingHosts}
              options={hostOptions}
              data-cy="create-event-host-select"
              showSearch
              filterOption={(input, option) =>
                String(option?.label ?? '')
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
            />
          )}
        </FormItem>

        <FormItem {...numberOfDays}>
          <RadioGroup
            options={numberOfDays.options}
            defaultValue={numberOfDays.initialValue}
            style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
            data-cy="create-event-number-of-days-radio"
          />
        </FormItem>

        <FormItem
          name={requiresRegistration.name}
          valuePropName="checked"
          style={{ marginBottom: 0 }}
        >
          <Checkbox
            style={{ marginBottom: 8 }}
            data-cy="create-event-requires-registration-checkbox"
          >
            {requiresRegistration.label}
          </Checkbox>
        </FormItem>

        {showRegistrationFields && (
          <div data-cy="create-event-registration-fields">
            <RegistrationFields />
          </div>
        )}
      </Form>

      <CreateOrganizationModal
        open={open}
        setOpen={setOpen}
        setSelectedOrganization={setSelectedOrganization}
      />
    </>
  );
};

export default CreateEventForm;
